import RouteTemplate from 'ember-route-template'
import { t } from 'ember-intl'
import type EWIPreviewsRoute from 'teacher-ui-client/subscription-type/writing-ideas/previews/route'
import Component from '@glimmer/component'
import { service } from '@ember/service'
import type { Log } from '@blakeelearning/log'
import type { LogArgument } from 'rollbar'
import NoResultsMessage from 'teacher-ui-client/components/no-results-message.gts'
import PageHeader from 'teacher-ui-client/components/page-header/index.gts'
import PhosphorIcon from 'teacher-ui-client/components/phosphor-icon.gts'
import { LinkTo } from '@ember/routing'
import UiButtonLinkTo from 'teacher-ui-client/components/ui-button/link-to.gts'
import { ButtonAppearance } from 'teacher-ui-client/components/ui-button/themes'
import { array, hash } from '@ember/helper'
import ExemplarBlock from 'teacher-ui-client/components/writing-legends/activity-blocks/exemplar-block.gts'
import ExemplarViewer from 'teacher-ui-client/components/writing-legends/activity-blocks/exemplar-viewer.gts'
import type { Annotation } from 'teacher-ui-client/components/writing-legends/activity-blocks/exemplar-viewer.gts'
import ImageBlock from 'teacher-ui-client/components/writing-legends/activity-blocks/image-block.gts'

interface Signature {
  Args: {
    model: ModelFor<EWIPreviewsRoute>
  }
}

function getWLImageFullPath(relativePath: string): string {
  return new URL(relativePath, 'https://cdn-editor.writinglegends.com/').href
}
export class EWIPreviewsPage extends Component<Signature> {
  @service log!: Log

  get activityId() {
    return this.args.model?.params.activity_id
  }

  get imageBlock() {
    return this.args.model?.activity?.blocks?.find((block) => block.block_type === 'Image')
  }

  get exemplarBlock() {
    const blockContent = this.args.model?.activity?.blocks?.find((block) => block.block_type === 'Exemplar')?.content
    if (blockContent) {
      try {
        return JSON.parse(blockContent) as { annotations: Annotation[]; sourceText: string }
      } catch (e) {
        this.log.error('EWI preview: failed to parse Exemplar block content', e as LogArgument)
        return null
      }
    }
    return null
  }

  get attachmentsBlock() {
    const blockContent = this.args.model?.activity?.blocks?.find((block) => block.block_type === 'Attachments')?.content
    if (blockContent) {
      try {
        const parsed = JSON.parse(blockContent) as { podcast1?: { src: string } }
        if (parsed.podcast1?.src) {
          return { src: getWLImageFullPath(parsed.podcast1.src) }
        }
      } catch (e) {
        this.log.error('EWI preview: failed to parse Attachments block content', e as LogArgument)
        return null
      }
    }
    return null
  }

  get activityTags(): string | undefined {
    return this.args.model?.activity?.tags
  }

  <template>
    <div class="grow">
      <PageHeader>
        <:left>
          <LinkTo @route="subscription-type.writing-ideas.index">
            <PhosphorIcon @icon="phosphor-caret-left" />
            <span class="underline">{{t "back"}}</span>
          </LinkTo>
        </:left>
        <:title>
          <h1>
            {{t "pages.excitingWritingIdeas.title"}}
          </h1>
        </:title>
      </PageHeader>
      {{#if @model.activity}}
        <div class="mx-auto flex w-2/3 flex-col">
          <UiButtonLinkTo
            @route="assignments.product.create"
            @models={{array "writing-legends"}}
            @query={{hash activityType="writing-legends-exciting-writing-ideas" writingCode=this.activityId}}
            @appearance={{ButtonAppearance.regular}}
            class="mx-auto mb-6"
            data-test-create-assignment
          >
            <PhosphorIcon @icon="phosphor-calendar-check-fill" class="mr-2" />
            {{t "pages.excitingWritingIdeas.previews.createAssignment"}}
          </UiButtonLinkTo>
          <h2 class="m-0 p-0 text-2xl">{{@model.activity.title}}</h2>
          <h4 class="text-lg font-normal">{{@model.activity.description}}</h4>
          {{#if this.imageBlock}}
            <ImageBlock class="my-6" @data={{this.imageBlock}} @tags={{this.activityTags}} />
          {{/if}}
          {{#if this.attachmentsBlock}}
            <div class="m-4 flex items-center justify-center gap-4" data-test-audio-player>
              <span class="text-xl font-bold">
                {{t "pages.excitingWritingIdeas.previews.playAudioIntroduction"}}
              </span>
              <audio controls src={{this.attachmentsBlock.src}}>
                {{! captions src not yet available — placeholder track to satisfy lint }}
                <track kind="captions" />
              </audio>
            </div>
          {{/if}}
          {{#if this.exemplarBlock}}
            <ExemplarBlock>
              <ExemplarViewer
                @annotations={{this.exemplarBlock.annotations}}
                @sourceText={{this.exemplarBlock.sourceText}}
              />
            </ExemplarBlock>
          {{/if}}
        </div>
      {{else}}
        <NoResultsMessage />
      {{/if}}
    </div>
  </template>
}

export default RouteTemplate(EWIPreviewsPage)
